import { useLayoutEffect, useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { ArrowUp } from "lucide-react";
import { cn } from "@nexus/ui";

/** Matches the server's limit on one question. */
export const ASK_NEXUS_MAX_MESSAGE_LENGTH = 4000;
/** The field grows with the question up to this many lines, then scrolls. */
const COMPOSER_MAX_LINES = 5;
const COMPOSER_LINE_HEIGHT_PX = 22;
/** Vertical padding inside the field, so one line is as tall as the send button and sits centred beside it. */
const COMPOSER_FIELD_PADDING_PX = 10;

/**
 * Sizes the field to its text, from one line to `COMPOSER_MAX_LINES`, in every
 * browser (CSS `field-sizing` is not supported everywhere). An empty field is
 * one line again.
 */
export function fitComposerHeight(field: HTMLTextAreaElement): void {
  const maxHeight = COMPOSER_MAX_LINES * COMPOSER_LINE_HEIGHT_PX + COMPOSER_FIELD_PADDING_PX;
  field.style.height = "auto";
  field.style.height = `${Math.min(field.scrollHeight, maxHeight)}px`;
  field.style.overflowY = field.scrollHeight > maxHeight ? "auto" : "hidden";
}

export function AskNexusComposer({
  tenantName,
  canSend,
  isRunning,
  canStop,
  isSending,
  isStopping,
  disabledReason,
  footnote,
  onSend,
  onStop,
}: {
  tenantName: string;
  canSend: boolean;
  isRunning: boolean;
  canStop: boolean;
  isSending: boolean;
  isStopping: boolean;
  /** Why the current chat takes no new question, if it doesn't. */
  disabledReason: string | null;
  /** Replaces the data reminder under the field, for a chat that takes no more questions. */
  footnote?: string | null;
  onSend: (message: string) => void;
  onStop: () => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState("");
  const fieldRef = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    if (fieldRef.current) fitComposerHeight(fieldRef.current);
  }, [draft]);
  const isBlocked = !canSend || disabledReason !== null;
  const message = draft.trim();
  const canSubmit = !isBlocked && !isRunning && !isSending && message.length > 0;

  const submit = () => {
    if (!canSubmit) return;
    onSend(message);
    setDraft("");
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    submit();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      submit();
    }
  };

  const placeholder = !canSend
    ? t("askNexus.composer.noSendAccess", "You can read your chats, but asking needs access to send questions.")
    : disabledReason ?? t("askNexus.composer.placeholder", "Ask anything about {{tenant}}", { tenant: tenantName });

  return (
    <form onSubmit={handleSubmit}>
      <div
        className={cn(
          "relative flex items-end gap-2 rounded-[24px] border border-(--an-hair-strong) bg-(--an-field) py-2 pr-2 pl-4 shadow-(--an-sh-chrome) transition-colors focus-within:border-(--an-hair-dashed)",
          isBlocked && "opacity-55",
          (isRunning || isSending) && "ask-nexus-composer-working",
        )}
        aria-busy={isRunning || isSending}
        data-testid="ask-nexus-composer-field"
      >
        <textarea
          ref={fieldRef}
          aria-label={t("askNexus.composer.label", "Your question")}
          className="block min-w-0 flex-1 resize-none overflow-hidden border-0 bg-transparent px-0 py-[5px] text-sm leading-[22px] text-(--an-ink) outline-none placeholder:text-(--an-ink-3) disabled:cursor-not-allowed"
          rows={1}
          maxLength={ASK_NEXUS_MAX_MESSAGE_LENGTH}
          placeholder={placeholder}
          value={draft}
          disabled={isBlocked}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={handleKeyDown}
        />
        <div className="flex flex-none items-center gap-1">
          {isRunning && canStop ? (
            <button
              type="button"
              className="grid size-8 place-items-center rounded-full bg-(--an-primary) text-(--an-primary-ink) transition-opacity disabled:cursor-default disabled:opacity-25"
              aria-label={t("askNexus.composer.stop", "Stop answer")}
              title={t("askNexus.composer.stop", "Stop answer")}
              disabled={isStopping}
              onClick={onStop}
            >
              <span className="size-3 rounded-[3px] bg-current" aria-hidden="true" />
            </button>
          ) : (
            <button
              type="submit"
              className="grid size-8 place-items-center rounded-full bg-(--an-primary) text-(--an-primary-ink) transition-opacity disabled:cursor-default disabled:opacity-25"
              aria-label={t("askNexus.composer.send", "Send question")}
              title={t("askNexus.composer.send", "Send question")}
              disabled={!canSubmit}
            >
              {isSending ? (
                <span className="ask-nexus-spin size-4" aria-hidden="true" />
              ) : (
                <ArrowUp className="size-4" strokeWidth={2} aria-hidden="true" />
              )}
            </button>
          )}
        </div>
      </div>
      <p className="mt-2 text-center text-[11px] leading-4 text-(--an-ink-3)">
        {footnote ?? t("askNexus.composer.footnote", "Uses {{tenant}} data only. Check key numbers before you share.", { tenant: tenantName })}
      </p>
    </form>
  );
}
